import React, { useCallback, useEffect, useState } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';

import { getInstallableApp } from 'client/api';
import { PageLoader } from 'client/blocks/Loader/PageLoader';
import { routes } from 'client/lib/routes';
import { Channel, InstallableApp } from 'client/model/skill';
import { isIframe } from 'client/utils';
import { tycoon } from 'client/utils/messages';

import { InstallableAppPage } from './InstallableAppPage';

interface InstallableAppPageContainerProps {
    skillId: string;
    channel: Channel;
}

export const InstallableAppPageContainer = ({ channel, skillId }: InstallableAppPageContainerProps) => {
    const [isLoading, setIsLoading] = useState(true);
    const [app, setApp] = useState<InstallableApp>();

    const setInstallableApp = useCallback(
        (appWithRevision?: InstallableApp) => {
            const actualRevision = appWithRevision?.revisions?.find((revision) => revision?.isActive);
            if (
                !!appWithRevision &&
                !!actualRevision &&
                typeof appWithRevision === 'object' &&
                typeof actualRevision === 'object'
            ) {
                const { channel, id, ...revisionSettings } = actualRevision;
                appWithRevision = { ...appWithRevision, ...revisionSettings };
            }
            setApp(appWithRevision);
        },
        [setApp],
    );

    const fetchApp = useCallback(async () => {
        setIsLoading(true);
        let fetchedApp: InstallableApp | undefined;
        try {
            fetchedApp = await getInstallableApp(skillId, channel);
        } catch (err) {
            console.error('@Error in fetch installableApp', err);
        }
        if (!fetchedApp && isIframe()) {
            tycoon.sendChatNotFound(skillId);
        }

        setInstallableApp(fetchedApp);
        setIsLoading(false);
    }, [channel, skillId, setInstallableApp]);

    useEffect(() => {
        fetchApp();
    }, [channel, skillId, fetchApp]);

    if (isLoading && !app) {
        return <PageLoader />;
    }

    if (!app) {
        // TODO: add 404 page
        return <Navigate to={routes.app.installableApps(channel)} replace />;
    }

    const page = (
        <InstallableAppPage
            installableApp={app}
            refreshInstallableApp={fetchApp}
            setInstallableApp={setInstallableApp}
        />
    );

    return (
        <Routes>
            <Route path="draft/*" element={page} />
            <Route path="published/*" element={page} />
            <Route path="*" element={page} />
        </Routes>
    );
};
